// "Forget this device" on the entrance (docs/08-app-design.md 5.9;
// app/art/welcome/PLAN.md sections 4.3 and 5): one sheet for the occupied
// /welcome and /locked (DeviceDoor). Before the typed FORGET it says what
// goes and what stays. The vault's fee reserve stays: its key is derived
// from the 24 words (docs/18-one-fee-reserve.md section 3), so the sheet
// lists it under "Not affected" and, the moment it opens, runs the network
// check (2.5) and reads the balance of the address the device recorded
// (`vaultInfo.feeWallet`). Only a legacy device (one that still stores the
// sealed per-device key of an earlier release, `sealedRelayer`) loses a
// key that controls funds: there "The older fee reserve key" is listed as
// removed, with its address, its balance and R48's warning that the 24
// words cannot recover it. A failed legacy read fails closed: the sheet
// then says the device may hold such a key. While that read has not
// answered, the typed confirmation is disabled (R93): FORGET can never run
// before the sheet knows what this device holds, and a recorded older key's
// warning is shown before its balance is read, so no network read gates
// it. When this browser cannot unlock
// (the door's last unlock refused with FeeKeyUnsupported, `legacyOffer`),
// the warnings point to the door's "Send the older reserve out" (docs/18
// 2.4) or to a browser that can unlock, never to an unlock that cannot
// happen here. The removed list is everything
// startDeletion (storage.ts) deletes: both databases, so this device's
// settings (RPC endpoint, sealed Jupiter API key, excluded venues,
// auto-lock) go with the keys and the ledger.
// - it does not forget anything itself: FORGET hands over to `onForget`,
//   and the door closes this sheet at once, so its "Keep it" is never on
//   screen while a deletion is being submitted (R62, now on both routes);
//   R47's order (lock, close connections, delete) is session.forget()'s;
// - focus goes to the FORGET field when it opens; "Keep it" and FORGET are
//   equal buttons, "Keep it" first.

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { assertDeployment } from '../deployment';
import { session, useSession } from '../session';
import { hasLegacyReserve } from '../storage';
import { OLDER_RESERVE_KIND } from './OlderReserve';
import { Address, Amount, Field, Sheet } from './primitives';

/** A balance read for the sheet: the figure, a refused network check (2.5), any other failure, or null while reading. */
type Reading = { lamports: bigint } | 'wrong-network' | 'unreadable' | null;

/**
 * Whether this device holds a legacy relayer envelope (docs/18 sections 3 and 6): true or false once the storage read
 * answers, 'unknown' when it failed, null while it runs. A failed read fails closed: the device may be legacy, and the
 * pages say so (MAYBE_OLDER_RESERVE_FORGET_SENTENCE) rather than claim there is no older key to lose.
 */
export type LegacyReading = boolean | 'unknown' | null;

/** The legacy reading for the unlocked pages' own Forget wording (Settings, Keys). */
export function useLegacyReserve(): LegacyReading {
  const [legacy, setLegacy] = useState<LegacyReading>(null);
  useEffect(() => {
    let cancelled = false;
    hasLegacyReserve().then(
      (v) => {
        if (!cancelled) setLegacy(v);
      },
      () => {
        if (!cancelled) setLegacy('unknown');
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);
  return legacy;
}

// The legacy sentences point to the move into the fee reserve, which the overview's older reserve notice offers
// (OlderReserve.tsx; docs/18 section 6, "Explicit, not automatic").

/** The legacy key's warning on the unlocked pages' Forget sheets (R48, kept for the older key only; docs/18 section 3). */
export const OLDER_RESERVE_FORGET_SENTENCE = 'This device also holds an older fee reserve key, which this deletes and your 24 words cannot recover: move its SOL into your fee reserve from the overview first.';

/** The same warning when the legacy read failed (fail closed): the device may hold such a key. */
export const MAYBE_OLDER_RESERVE_FORGET_SENTENCE = 'This device may also hold an older fee reserve key that your 24 words cannot recover, which this would delete: move any SOL it holds into your fee reserve from the overview first.';

/** The fail-closed warning when this browser cannot unlock (the door's `legacyOffer`): the overview is out of reach here. */
const MAYBE_OLDER_RESERVE_NO_UNLOCK_SENTENCE = 'This device may also hold an older fee reserve key that your 24 words cannot recover, which this would delete: move any SOL it holds into your fee reserve first, from a browser that can unlock this device.';

/** While the legacy read has not answered (R93): said on every Forget surface, whose confirmation is disabled meanwhile (`forgetConfirmDisabled`). */
export const CHECKING_OLDER_RESERVE_SENTENCE = 'Checking this device for an older fee reserve key…';

/** The Forget sentence for a legacy reading: the warning, the fail-closed warning, the checking line while the read has not answered, or nothing (no older key). */
export function olderReserveForgetSentence(legacy: LegacyReading): string {
  return legacy === true ? OLDER_RESERVE_FORGET_SENTENCE : legacy === 'unknown' ? MAYBE_OLDER_RESERVE_FORGET_SENTENCE : legacy === null ? CHECKING_OLDER_RESERVE_SENTENCE : '';
}

/**
 * Whether a Forget surface's destructive button is disabled (R93): until the typed word is FORGET, and while the legacy
 * read has not answered (null), since the device may hold an older key the words cannot rebuild and its warning is not
 * on screen yet. A failed read ('unknown') enables it, with the fail-closed warning shown. No balance read gates it.
 */
export function forgetConfirmDisabled(word: string, legacy: LegacyReading): boolean {
  return word !== 'FORGET' || legacy === null;
}

/** The "This device" section's clause naming the older key among what Forget deletes. */
export function olderReserveClause(legacy: LegacyReading): string {
  return legacy === true ? ', its older fee reserve key' : legacy === 'unknown' ? ', any older fee reserve key it may hold' : '';
}

function balanceGap(reading: Exclude<Reading, { lamports: bigint } | null>): string {
  return reading === 'wrong-network' ? 'its balance could not be read on this network' : 'its balance could not be read';
}

/**
 * `legacyOffer`: the door's last unlock was refused with FeeKeyUnsupported, so this browser cannot unlock, and the
 * door offers "Send the older reserve out" on a legacy device instead (docs/18 2.4).
 */
export function ForgetSheet({ open, onClose, onForget, legacyOffer = false }: { open: boolean; onClose: () => void; onForget: () => void; legacyOffer?: boolean }) {
  const { info } = useSession();
  // Null until the storage read answers. A failed read fails closed: a recorded legacy address (only an earlier
  // release wrote one, docs/18 section 3) still counts as legacy, and without one the device may be legacy ('unknown').
  const [legacy, setLegacy] = useState<LegacyReading>(null);
  const [reserve, setReserve] = useState<Reading>(null);
  const [older, setOlder] = useState<Reading>(null);
  const [word, setWord] = useState('');
  const field = useRef<HTMLInputElement>(null);

  // The moment the sheet opens: is this a legacy device, then one network check (2.5) and the balances of the
  // recorded fee reserve and, on a legacy device, of the older key. The figures come before the confirmation (R48).
  useEffect(() => {
    if (!open) return;
    setLegacy(null);
    setReserve(null);
    setOlder(null);
    setWord('');
    // the dialog is open by now (the Sheet's own effect runs first): the typed confirmation takes focus
    field.current?.focus();
    const feeWallet = info?.feeWallet;
    const relayer = info?.relayer;
    let cancelled = false;
    void (async () => {
      const isLegacy: LegacyReading = await hasLegacyReserve().catch(() => (relayer !== undefined ? true : 'unknown'));
      if (cancelled) return;
      setLegacy(isLegacy);
      const olderAddress = isLegacy === true ? relayer : undefined;
      if (!feeWallet && !olderAddress) return;
      const rpc = session.rpc();
      // assertDeployment throws only WrongNetwork, a failed genesis read included
      const onNetwork = await assertDeployment(rpc).then(
        () => true,
        () => false,
      );
      if (cancelled) return;
      const read = (address: string | undefined): Promise<Reading> => {
        if (!address) return Promise.resolve(null);
        if (!onNetwork) return Promise.resolve('wrong-network');
        return rpc.getBalance(address).then(
          (lamports): Reading => ({ lamports }),
          (): Reading => 'unreadable',
        );
      };
      const [a, b] = await Promise.all([read(feeWallet), read(olderAddress)]);
      if (cancelled) return;
      setReserve(a);
      setOlder(b);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const feeWallet = info?.feeWallet;
  const relayer = info?.relayer;

  // The vault's fee reserve: it stays, whatever this device holds (docs/18 section 3).
  let reserveLine: ReactNode;
  if (!feeWallet) {
    // set up before this release and never unlocked under it: no address is recorded yet
    reserveLine = 'Your fee reserve is not removed: it is derived from your 24 words, which open it on any device. This device has not recorded its address; the overview shows it after an unlock.';
  } else if (reserve === null) {
    reserveLine = 'Reading the fee reserve’s balance…';
  } else if (typeof reserve === 'object') {
    reserveLine = (
      <>
        Your fee reserve is not removed. It holds <Amount raw={reserve.lamports} decimals={9} symbol="SOL" /> at <Address value={feeWallet} kind="fee reserve" />, and your 24 words open it on any device.
      </>
    );
  } else {
    reserveLine = (
      <>
        Your fee reserve is not removed. It is at <Address value={feeWallet} kind="fee reserve" />, and {balanceGap(reserve)}. Your 24 words open it on any device.
      </>
    );
  }

  // A legacy device's older key (docs/18 sections 3 and 6): removed with the device, and the words cannot rebuild it.
  let olderLine: ReactNode = null;
  if (legacy === null) {
    // the storage read has not answered: the confirmation below is disabled until it does (R93)
    olderLine = CHECKING_OLDER_RESERVE_SENTENCE;
  } else if (legacy === 'unknown') {
    // the storage read failed and no legacy address is recorded: fail closed, the device may be legacy
    olderLine = legacyOffer ? MAYBE_OLDER_RESERVE_NO_UNLOCK_SENTENCE : MAYBE_OLDER_RESERVE_FORGET_SENTENCE;
  } else if (legacy) {
    // R48's warning, kept for the older key: the overview's notice moves its SOL into the fee reserve (section 6); a
    // browser that cannot unlock has only the door's escape hatch (2.4), which sends to an address the user types
    const warning = legacyOffer
      ? 'Your 24 words cannot recover it: send its SOL out first with Send the older reserve out, or move it into your fee reserve from a browser that can unlock this device.'
      : 'Your 24 words cannot recover it: unlock and move its SOL into your fee reserve first.';
    if (!relayer) {
      // set up before R48 recorded its address; the overview's "Open it to see its balance" opens the key and records it,
      // and the escape hatch opens it to learn its address too (2.4, the pre-R48 case)
      olderLine = legacyOffer
        ? 'This device holds an older fee reserve key whose address was not recorded, and your 24 words cannot recover it. Send its SOL out first with Send the older reserve out, which opens it and shows its balance, or keep this device; a browser that can unlock it opens the key from the overview.'
        : 'This device holds an older fee reserve key whose address was not recorded, and your 24 words cannot recover it. Open it to see its balance: unlock, open it from the overview, and move its SOL into your fee reserve first.';
    } else if (older === null) {
      // the warning does not wait for the network (R93): the balance, once read, adds its figure
      olderLine = (
        <>
          The older fee reserve key on this device is at <Address value={relayer} kind={OLDER_RESERVE_KIND} />; its balance is being read. {warning}
        </>
      );
    } else if (typeof older === 'object') {
      olderLine =
        older.lamports > 0n ? (
          <>
            The older fee reserve key on this device holds <Amount raw={older.lamports} decimals={9} symbol="SOL" /> at <Address value={relayer} kind={OLDER_RESERVE_KIND} />. {warning}
          </>
        ) : (
          <>
            The older fee reserve key on this device, at <Address value={relayer} kind={OLDER_RESERVE_KIND} />, holds nothing. Your 24 words cannot recover it: SOL sent to that address after Forget is lost.
          </>
        );
    } else {
      olderLine = (
        <>
          The older fee reserve key on this device is at <Address value={relayer} kind={OLDER_RESERVE_KIND} />, and {balanceGap(older)}. {warning}
        </>
      );
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Forget this device">
      <h2>Forget this device</h2>
      <p className="forget-lede">This deletes the vault&rsquo;s keys from this browser. It cannot be undone.</p>
      <div className="forget-lists">
        <div>
          <h3>Removed from this device</h3>
          <ul>
            <li>The sealed vault key</li>
            {legacy === true ? <li>The older fee reserve key</li> : legacy === 'unknown' ? <li>Any older fee reserve key this device may hold</li> : null}
            <li>The record of used one-time keys and this device&rsquo;s activity history</li>
            <li>This device&rsquo;s settings, including its RPC endpoint and Jupiter API key</li>
          </ul>
        </div>
        <div>
          <h3>Not affected</h3>
          <ul>
            <li>The vault and its funds on chain</li>
            <li>Your fee reserve and its SOL</li>
            <li>Your 24 words, which restore both</li>
          </ul>
        </div>
      </div>
      <p className="reserve">{reserveLine}</p>
      {olderLine ? <p className="reserve">{olderLine}</p> : null}
      <Field id="forget-word" label="Type FORGET to confirm">
        <input ref={field} id="forget-word" className="input" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} />
      </Field>
      <div className="forget-acts">
        <button type="button" className="cta cta-plain" onClick={onClose}>
          Keep it
        </button>
        <button type="button" className="cta cta-danger" disabled={forgetConfirmDisabled(word, legacy)} onClick={onForget}>
          Forget this device
        </button>
      </div>
    </Sheet>
  );
}
